import { useCallback, useState } from 'react';

/**
 * Reusable Pagination Controls component.
 * Clean, flat design — no transforms, explicit style resets.
 */
export function Pagination({ pagination, onPageChange, onLimitChange, className = '' }) {
  const handlePageChange = useCallback((newPage) => {
    if (pagination && newPage >= 1 && newPage <= pagination.total_pages) {
      onPageChange(newPage);
    }
  }, [pagination, onPageChange]);

  if (!pagination || !pagination.total) return null;

  const { page, limit, total, total_pages, has_next, has_prev } = pagination;

  // Generate visible page numbers (max 5)
  const getPages = () => {
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(total_pages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);
  const pages = getPages();

  // Base button style — flat, no transforms
  const btnBase = "inline-flex items-center justify-center min-w-[36px] h-9 text-sm font-medium rounded-md transition-colors duration-150 select-none";
  const btnDefault = `${btnBase} border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white`;
  const btnActive = `${btnBase} border border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-500/25`;
  const btnDisabled = `${btnBase} border border-slate-700/50 bg-slate-800/50 text-slate-600 cursor-not-allowed`;
  const btnNav = `${btnBase} px-2 border border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white`;

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-2 ${className}`}
      style={{ transform: 'none', perspective: 'none' }}
      data-testid="pagination-controls"
    >
      {/* Left: Item count */}
      <p className="text-sm text-slate-400 whitespace-nowrap">
        Showing{' '}
        <span className="text-slate-200 font-medium">{startItem}</span>
        {' '}-{' '}
        <span className="text-slate-200 font-medium">{endItem}</span>
        {' '}of{' '}
        <span className="text-slate-200 font-medium">{total.toLocaleString()}</span>
      </p>

      {/* Center: Page buttons */}
      <nav className="flex items-center gap-1.5" style={{ transform: 'none' }}>
        {/* Prev */}
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={!has_prev}
          className={has_prev ? btnNav : btnDisabled}
          data-testid="pagination-prev"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* First page + ellipsis */}
        {pages[0] > 1 && (
          <>
            <button onClick={() => handlePageChange(1)} className={btnDefault}>1</button>
            {pages[0] > 2 && <span className="px-1 text-slate-500 text-sm">...</span>}
          </>
        )}

        {/* Page numbers */}
        {pages.map(p => (
          <button
            key={p}
            onClick={() => handlePageChange(p)}
            className={p === page ? btnActive : btnDefault}
            data-testid={`pagination-page-${p}`}
          >
            {p}
          </button>
        ))}

        {/* Last page + ellipsis */}
        {pages[pages.length - 1] < total_pages && (
          <>
            {pages[pages.length - 1] < total_pages - 1 && <span className="px-1 text-slate-500 text-sm">...</span>}
            <button onClick={() => handlePageChange(total_pages)} className={btnDefault}>{total_pages}</button>
          </>
        )}

        {/* Next */}
        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={!has_next}
          className={has_next ? btnNav : btnDisabled}
          data-testid="pagination-next"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </nav>

      {/* Right: Per-page selector */}
      <div className="flex items-center gap-2 text-sm text-slate-400 whitespace-nowrap">
        <span>Rows</span>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value))}
          className="h-9 px-2 border border-slate-700 bg-slate-800 text-slate-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
          data-testid="pagination-limit-select"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  );
}

/**
 * Hook for managing pagination state.
 */
export function usePagination(defaultLimit = 20) {
  const [paginationState, setPaginationState] = useState({
    page: 1,
    limit: defaultLimit,
    pagination: null,
  });

  const setPage = useCallback((newPage) => {
    setPaginationState(prev => ({ ...prev, page: newPage }));
  }, []);

  const setLimit = useCallback((newLimit) => {
    setPaginationState(prev => ({ ...prev, page: 1, limit: newLimit }));
  }, []);

  const setPagination = useCallback((serverPagination) => {
    setPaginationState(prev => ({ ...prev, pagination: serverPagination }));
  }, []);

  const resetPage = useCallback(() => {
    setPaginationState(prev => ({ ...prev, page: 1 }));
  }, []);

  return {
    page: paginationState.page,
    limit: paginationState.limit,
    pagination: paginationState.pagination,
    setPage,
    setLimit,
    setPagination,
    resetPage,
  };
}
